import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ar from "../../../assets/saudi-arabia.png";
import en from "../../../assets/united-kingdom.png";

const lngs = [
  {
    code: "ar",
    nativeName: "العربية",
    logo: ar,
  },
  {
    code: "en",
    nativeName: "English",
    logo: en,
  },
];

interface LangSelectorProps {
  variant?: "dropdown" | "buttons";
}

export const LangSelector = ({ variant = "dropdown" }: LangSelectorProps) => {
  const { i18n } = useTranslation();
  const [isLangOpen, setIsLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = lngs.find((lng) => lng.code === i18n.language) || lngs[1];
  const isRTL = i18n.language === "ar";

  useEffect(() => {
    if (variant !== "dropdown") return;

    const handleClickOutside = (event: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(event.target as Node)) {
        setIsLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [variant]);

  const toggleLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setIsLangOpen(false);
  };

  if (variant === "buttons") {
    return (
      <div className="pt-4 mt-2 border-t border-white/20">
        <p className="mb-2 text-sm text-white/70">
          {isRTL ? "اختر اللغة" : "Select Language"}
        </p>
        <div className="flex gap-4">
          {lngs.map((lng) => (
            <button
              key={lng.code}
              onClick={() => toggleLanguage(lng.code)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                i18n.language === lng.code
                  ? "bg-hero-yellow text-burgundy font-bold"
                  : "bg-white/10 text-white hover:bg-white/20"
              }`}
            >
              <img
                src={lng.logo}
                alt={lng.nativeName}
                className="object-cover w-5 h-5 rounded-full"
              />
              {lng.nativeName}
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={langRef}>
      <button
        onClick={() => setIsLangOpen(!isLangOpen)}
        className="flex items-center gap-2 font-medium text-white transition-colors hover:text-hero-yellow"
      >
        <img
          src={currentLang.logo}
          alt={currentLang.nativeName}
          className="object-cover w-5 h-5 rounded-full"
        />
        {currentLang.nativeName}
        <svg
          className={`w-4 h-4 transition-transform ${isLangOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isLangOpen && (
        <div className="absolute top-full mt-2 bg-white rounded-lg shadow-lg py-2 min-w-[140px] z-50">
          {lngs.map((lng) => (
            <button
              key={lng.code}
              onClick={() => toggleLanguage(lng.code)}
              className={`w-full flex items-center gap-3 px-4 py-2 hover:bg-gray-100 ${
                i18n.language === lng.code
                  ? "text-burgundy font-bold"
                  : "text-gray-700"
              }`}
            >
              <img
                src={lng.logo}
                alt={lng.nativeName}
                className="object-cover w-5 h-5 rounded-full"
              />
              {lng.nativeName}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
